"use client";

import React, { useEffect, useRef } from "react";
import type { SearchFieldProps } from "./search-field";
import styles from "./search-field.module.css";
import {
  ClearGlyph,
  LeadingSlot,
  SearchGlyph,
  TrailingSlot,
  searchFieldChrome,
  searchFieldClasses,
  warnAboutFocusableSlotContent
} from "./search-field.shared";
import { withSafeRender } from "../safe-render";

/**
 * SearchFieldClient — the same pill with callbacks, for call sites that filter
 * a list as the reader types. Unlike the server twin it does not navigate:
 * `onSubmit` gets the value, and its clear empties the field for real rather
 * than restoring `defaultValue` the way a `type="reset"` does.
 */
/**
 * The shared props are inherited rather than retyped, so adding one to the
 * atom cannot leave this file silently not supporting it.
 */
interface SearchFieldClientBaseProps
  extends Omit<SearchFieldProps, "action" | "defaultValue"> {
  /** Native fallback destination when no `onSubmit` is given. */
  action?: string;
  /**
   * Redeclared for the description alone: this clear empties the field
   * outright, reports `""` and returns focus to the input.
   */
  clearable?: boolean;
  /** Fires on Enter with the current value, instead of navigating. */
  onSubmit?: (value: string) => void;
  /**
   * ARIA for a suggestion list drawn around this pill. Passing `ariaControls`
   * is what promotes the input to `role="combobox"`; without it the field
   * stays a plain searchbox.
   */
  ariaControls?: string;
  /** Whether that list is showing. Only meaningful with `ariaControls`. */
  ariaExpanded?: boolean;
  /** The id of the highlighted option, as the reader arrows through it. */
  ariaActiveDescendant?: string;
  /** What the list offers. `list` is the DIRECTV search behaviour. */
  ariaAutocomplete?: "none" | "inline" | "list" | "both";
  // Generated by Copilot- Start - 2026-10-06 11:14:48
  /**
   * The clear control's reported placement, written with its lowercased label
   * to `data-report-linkposition` / `data-report-linkname` for a host that
   * reports clicks itself. Absent, neither is written; nothing here pushes.
   */
  clearLinkPosition?: string;
  // Generated by Copilot- End - 2026-10-06 11:14:48
}

/** Controlled: the consumer owns the value, so it must also take it back. */
interface ControlledValueProps {
  /** The value the consumer holds. Rendered as-is on every keystroke. */
  value: string;
  /** Where the consumer receives each keystroke. Required when controlled. */
  onValueChange: (value: string) => void;
  defaultValue?: never;
}

/** Uncontrolled: the DOM node holds the value and `defaultValue` seeds it. */
interface UncontrolledValueProps {
  value?: never;
  /** Uncontrolled starting value. */
  defaultValue?: string;
  /** Fires on every keystroke with the current value. */
  onValueChange?: (value: string) => void;
}

export type SearchFieldClientProps = SearchFieldClientBaseProps &
  (ControlledValueProps | UncontrolledValueProps);

function SearchFieldClientImpl({
  id,
  label,
  icon = false,
  startIcon,
  endIcon,
  surface,
  rim,
  name = "q",
  value,
  defaultValue,
  onValueChange,
  onSubmit,
  clearable = false,
  clearLabel = "Clear search",
  ariaControls,
  ariaExpanded,
  ariaActiveDescendant,
  ariaAutocomplete,
  clearLinkPosition,
  action,
  method = "get",
  maxLength,
  disabled = false,
  autoFocus = false,
  ariaLabel = "Site search",
  className
}: SearchFieldClientProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  /* Frozen at mount rather than re-read each render: a consumer flipping
     between the two modes mid-life makes React keep whatever the DOM node
     happened to hold, silently and without throwing. */
  const isControlled = useRef(value !== undefined).current;

  if (
    process.env.NODE_ENV !== "production" &&
    isControlled !== (value !== undefined)
  ) {
    console.error(
      `[SearchFieldClient] "${id}" switched between controlled and uncontrolled. ` +
        `The mode is fixed at mount; pass "" rather than undefined to clear a ` +
        `controlled field.`
    );
  }

  const classes = searchFieldClasses({
    leading: icon || Boolean(startIcon),
    trailing: clearable || Boolean(endIcon),
    className
  });

  // Same for the appearance attributes, and their dev-only diagnostics.
  const chrome = searchFieldChrome({
    id,
    surface,
    rim,
    icon,
    startIcon,
    endIcon,
    clearable
  });

  /* After paint, so a slot's node is in the DOM to inspect. Re-run when
     either slot's content changes. */
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      warnAboutFocusableSlotContent(formRef.current, id);
    }
  }, [id, startIcon, endIcon]);

  /* With neither `onSubmit` nor `action`, a bare submit would reload the page
     the reader is on. */
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      event.preventDefault();
      onSubmit(inputRef.current?.value ?? "");
    } else if (!action) {
      event.preventDefault();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange?.(event.target.value);
  };

  /* Focus returns to the input because the clicked control is about to
     disappear from under the cursor. */
  const handleClear = () => {
    /* Uncontrolled only: there is no owned value to re-render from, so the
       node is blanked by hand. Doing it when controlled would fight the
       owner's own re-render for a frame. */
    if (!isControlled && inputRef.current) {
      inputRef.current.value = "";
    }
    onValueChange?.("");
    inputRef.current?.focus();
  };

  return (
    <form
      action={action}
      aria-label={ariaLabel}
      className={classes}
      method={method}
      onSubmit={handleSubmit}
      ref={formRef}
      role="search"
      {...chrome}
    >
      {startIcon ? <LeadingSlot>{startIcon}</LeadingSlot> : icon && <SearchGlyph />}
      {/* Input before label: the float rides an adjacent-sibling selector. */}
      <input
        autoComplete="off"
        autoFocus={autoFocus}
        className={styles.input}
        disabled={disabled}
        id={id}
        maxLength={maxLength}
        name={name}
        onChange={handleChange}
        placeholder=" "
        ref={inputRef}
        type="search"
        {...(ariaControls ?
          {
              role: "combobox",
              "aria-controls": ariaControls,
              "aria-expanded": ariaExpanded ?? false,
              "aria-autocomplete": ariaAutocomplete ?? "list",
              ...(ariaActiveDescendant ?
                { "aria-activedescendant": ariaActiveDescendant } :
                {})
            } :
          {})}
        {...(isControlled ? { value: value ?? "" } : { defaultValue })}
      />
      <label className={styles.label} htmlFor={id}>
        <span className={styles.labelText}>{label}</span>
      </label>
      {clearable && (
        <button
          aria-label={clearLabel}
          {...(clearLinkPosition ?
            {
              "data-report-linkname": clearLabel.trim().toLowerCase(),
              "data-report-linkposition": clearLinkPosition
            } :
            {})}
          className={styles.clear}
          disabled={disabled}
          onClick={handleClear}
          type="button"
        >
          <ClearGlyph />
        </button>
      )}
      {!clearable && endIcon ? <TrailingSlot>{endIcon}</TrailingSlot> : null}
    </form>
  );
}
export const SearchFieldClient = withSafeRender(SearchFieldClientImpl, "SearchFieldClient");

export default SearchFieldClient;
